/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo } from 'react';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { useHasUserEngagedWithBlueprints } from '../../controllers/blueprints-engagement/index.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';

const heading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-discovery-section.heading',

	defaultMessage: 'Jira Product Discovery',
	description: 'Navigation group heading for JPD settings items',
});

const accessItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-discovery-section.access-item',

	defaultMessage: 'Access',
	description: 'Navigation item which links to a JPD user access configuration settings page',
});

const globalFieldsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-discovery-section.global-fields-item',

	defaultMessage: 'Global fields',
	description: 'Navigation item which links to a JPD global fields page',
});

const globalViewsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-discovery-section.global-views-item',

	defaultMessage: 'Global views',
	description: 'Navigation item which links to the JPD global views page',
});

const globalViewsItemLozenge = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-discovery-section.global-views-new-lozenge',

	defaultMessage: 'New',
	description: 'Lozenge for the new Global views feature',
});

const viewBlueprintsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-discovery-section.view-blueprints-item',

	defaultMessage: 'Pre-built views',
	description: 'Navigation item which links to a JPD view blueprints setting page',
});

const viewBlueprintsItemLozenge = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-discovery-section.new-lozenge',

	defaultMessage: 'New',
	description: 'Lozenge for the new Pre-built views feature',
});

const datesItem = defineMessage({
	id: 'spa-apps-sidebar-global-settings-products.menu.product-discovery.dates-item',

	defaultMessage: 'Dates',
	description: 'Navigation item which links to a JPD dates settings page',
});

const betaFeaturesItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.product-discovery-section.beta-features-item',

	defaultMessage: 'Beta features',
	description: 'Navigation item which links to a JPD beta features settings page',
});

const ProductDiscoverySection = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();
	const hasUserEngagedWithBlueprints = useHasUserEngagedWithBlueprints();

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(heading)}</MenuSectionHeading>
			<MenuList>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'access' }}
					isSelected={currentRoute.name === 'polaris-settings-access'}
					href="/jira/settings/products/jira-product-discovery/access"
				>
					{formatMessage(accessItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'globalFields' }}
					isSelected={currentRoute.name === 'polaris-settings-global-fields'}
					href="/jira/settings/products/jira-product-discovery/global-fields"
				>
					{formatMessage(globalFieldsItem)}
				</LinkMenuItem>
				{fg('polaris_global_views') && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'globalViews' }}
						isSelected={
							currentRoute.name === 'polaris-settings-global-views-list' ||
							currentRoute.name === 'polaris-settings-global-views-details'
						}
						href="/jira/settings/products/jira-product-discovery/global-views"
						elemAfter={
							<Lozenge appearance="discovery">{formatMessage(globalViewsItemLozenge)}</Lozenge>
						}
					>
						{formatMessage(globalViewsItem)}
					</LinkMenuItem>
				)}
				{fg('jpd-view-templates-sample-project') && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'viewBlueprints' }}
						isSelected={currentRoute.name === 'polaris-settings-view-blueprints'}
						href="/jira/settings/products/jira-product-discovery/view-blueprints"
						elemAfter={
							hasUserEngagedWithBlueprints ? null : (
								<Lozenge appearance="new">{formatMessage(viewBlueprintsItemLozenge)}</Lozenge>
							)
						}
					>
						{formatMessage(viewBlueprintsItem)}
					</LinkMenuItem>
				)}
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'jpdDates' }}
					isSelected={currentRoute.name === 'polaris-settings-dates'}
					href="/jira/settings/products/jira-product-discovery/dates"
				>
					{formatMessage(datesItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'betaFeatures' }}
					isSelected={currentRoute.name === 'polaris-settings-early-access-features'}
					href="/jira/settings/products/jira-product-discovery/early-access-features"
				>
					{formatMessage(betaFeaturesItem)}
				</LinkMenuItem>
			</MenuList>
		</MenuSection>
	);
};

const ExportedComponent: React.MemoExoticComponent<() => React.JSX.Element> =
	memo(ProductDiscoverySection);
export default ExportedComponent;
